"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useState } from "react";
import { CalendarDays, Check, ChevronLeft, ChevronRight, Droplet, Flame, Leaf, Loader, MapPin, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const workshopOptions = [{
  value: "Executive Search",
  name: "Executive Search",
  latin: "Leadership Acquisition",
  note: "Strategic placement for C-suite and senior management roles.",
  icon: Leaf
}, {
  value: "Technical Recruitment",
  name: "Technical Recruitment",
  latin: "Engineering Talent",
  note: "Sourcing elite developers and technical architects.",
  icon: Moon
}, {
  value: "Financial Staffing",
  name: "Financial Staffing",
  latin: "Fiscal Expertise",
  note: "Connecting top-tier finance professionals with firms.",
  icon: Droplet
}, {
  value: "Other",
  name: "Bespoke Inquiry",
  latin: "Custom Solutions",
  note: "Tailored recruitment strategies for unique business needs.",
  icon: Flame
}];
const fieldNotes = [{
  label: "OFFICE",
  value: "Nexus HQ, Financial District"
}, {
  label: "REGION",
  value: "Global Operations"
}, {
  label: "COORDINATES",
  value: "40.7128° N, 74.0060° W"
}, {
  label: "STANDARDS",
  value: "Professional Excellence"
}, {
  label: "REQUIRE",
  value: "CV & Professional Portfolio"
}, {
  label: "HOURS",
  value: "Mon–Fri 9–6 · Sat By Appt"
}];
export default function CareerLaunchpad() {
  return <section data-section-id="2053" id="career-launchpad" className="relative bg-background text-foreground py-28 md:py-40 overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 size-2 rounded-lg bg-primary" aria-hidden="true" />

      {}
      <CornerMark className="top-6 left-6" />
      <CornerMark className="top-6 right-6" />
      <CornerMark className="bottom-6 left-6" />
      <CornerMark className="bottom-6 right-6" />

      <div className="relative mx-auto max-w-[1440px] px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-8">
            <FormProvider formKey="career_launchpad_form_home" formId="career_launchpad_form_home" sectionName="career-launchpad" defaultValues={{
            service_type: "",
            full_name: "",
            email: "",
            phone: "",
            preferred_date: "",
            message: "",
            consent: false
          }} validationRules={{
            full_name: {
              required: true,
              minLength: 2,
              maxLength: 100
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            preferred_date: {
              required: true
            },
            service_type: {
              required: true
            },
            consent: {
              required: true
            }
          }} submitText="Submit Application" successMessage="Application received. Our consultants will review your profile and contact you shortly." errorMessage="Submission failed. Please verify your details and try again.">
              <InquiryForm />
            </FormProvider>
          </div>

          {}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-foreground/50 mb-8 flex items-center gap-2">
                <span>AGENCY NOTES</span>
                <span className="h-px flex-1 bg-foreground/20" />
                <span className="size-1.5 rounded-lg bg-primary" />
              </p>

              <ul className="space-y-6">
                {fieldNotes.map((note, i) => <li key={note.label} data-index={i} className="group">
                    <div className="flex items-start gap-3">
                      <span className="font-mono text-[10px] tracking-[0.16em] text-foreground/40 pt-1 w-20 shrink-0">
                        {note.label}
                      </span>
                      <span className="h-px flex-1 bg-foreground/15 mt-2.5" />
                    </div>
                    <p className="font-default italic text-base text-foreground/85 mt-2 pl-[88px] flex items-center gap-2">
                      {note.value}
                      <span className="size-1.5 rounded-lg bg-primary inline-block" />
                    </p>
                  </li>)}
              </ul>

              <div className="mt-12 pt-8 border-t border-foreground/15">
                <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-foreground/50 mb-3">
                  SUPPORT
                </p>
                <p className="font-default italic text-sm text-foreground/70 leading-relaxed">
                  Our recruitment team responds to all inquiries within 24 business hours.
                </p>
                <p className="font-mono text-xs tracking-wider text-foreground/80 mt-3 flex items-center gap-2">
                  <MapPin className="size-3.5" />
                  careers@nexustalent.com
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>;
}
function CornerMark({
  className
}: {
  className?: string;
}) {
  return <div className={`absolute size-3 ${className ?? ""}`} aria-hidden="true">
      <span className="absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 bg-foreground/40" />
      <span className="absolute top-1/2 left-0 right-0 h-px -translate-y-1/2 bg-foreground/40" />
    </div>;
}
function StepIndicator({
  step
}: {
  step: number;
}) {
  const steps = [{
    num: "01",
    label: "SERVICE"
  }, {
    num: "02",
    label: "PROFILE"
  }, {
    num: "03",
    label: "SUBMIT"
  }];
  return <div className="flex items-stretch gap-0 mb-12 flex-wrap md:flex-nowrap">
      {steps.map((s, i) => {
      const active = i === step;
      const done = i < step;
      return <div key={s.num} data-index={i} className="flex items-center flex-1 min-w-[120px]">
            <div className="flex flex-col gap-2 py-2 pr-4">
              <div className="flex items-center gap-2">
                <span className={`font-mono text-xs tracking-[0.16em] ${active ? "text-primary" : done ? "text-foreground/70" : "text-foreground/40"}`}>
                  {s.num}
                </span>
                {done && <Check className="size-3 text-primary" />}
              </div>
              <span className={`font-mono text-[11px] tracking-[0.18em] ${active ? "text-foreground" : "text-foreground/50"}`}>
                {s.label}
              </span>
              {active && <div className="flex items-center gap-1 mt-1">
                  <span className="h-px w-12 bg-primary" />
                  <span className="size-1.5 rounded-lg bg-primary" />
                </div>}
            </div>
            {i < steps.length - 1 && <span className="h-px flex-1 bg-foreground/20 self-center hidden md:block" />}
          </div>;
    })}
    </div>;
}
function InquiryForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const [step, setStep] = useState(0);
  const {
    control,
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-10 border border-primary/40 bg-muted">
        <p className="font-mono text-xs tracking-[0.18em] text-primary mb-4">§ 03 — SUBMITTED</p>
        <p className="font-default text-2xl text-foreground leading-snug mb-6">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="bg-transparent border-foreground/40 text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary font-mono text-xs tracking-[0.08em] uppercase">
          New Application
        </Button>
      </div>;
  }
  return <div ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
      {}
      <p className="font-mono text-xs tracking-[0.18em] text-foreground/60 mb-8 uppercase">
        § 03 — TALENT REGISTRATION
      </p>

      {}
      <h2 className="font-default text-[clamp(2.25rem,4.8vw,4.5rem)] font-bold leading-[1.02] tracking-tight text-foreground mb-12">
        Join the
        <br />
        <span className="italic text-primary">Nexus</span>
      </h2>

      <StepIndicator step={step} />

      <Form onSubmit={handleSubmit} className="space-y-10">
        {}
        {step === 0 && <FormField control={control} name="service_type" rules={{
        required: "Please select a service"
      }} render={({
        field
      }) => <FormItem>
                <FormLabel className="font-mono text-[11px] tracking-[0.18em] uppercase text-foreground/60">
                  Select Specialization
                </FormLabel>
                <FormControl>
                  <RadioGroup value={field.value} onValueChange={field.onChange} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                    {workshopOptions.map((opt, i) => {
              const Icon = opt.icon;
              const selected = field.value === opt.value;
              return <label key={opt.value} data-index={i} htmlFor={`svc-${i}`} className={`relative cursor-pointer p-7 border transition-colors duration-300 ${selected ? "border-primary bg-muted" : "border-foreground/15 bg-white hover:border-foreground/40"}`}>
                          <RadioGroupItem value={opt.value} id={`svc-${i}`} className="sr-only" />
                          {}
                          <CornerMark className="top-2 left-2 size-2" />
                          <CornerMark className="top-2 right-2 size-2" />
                          <CornerMark className="bottom-2 left-2 size-2" />
                          <CornerMark className="bottom-2 right-2 size-2" />

                          <Icon className="size-7 text-foreground mb-5" strokeWidth={1.25} />
                          <p className="font-default text-xl text-foreground leading-tight">
                            {opt.name}
                          </p>
                          <p className="font-default italic text-sm text-foreground/55 mt-1">
                            {opt.latin}
                          </p>
                          <p className="text-sm text-foreground/70 mt-3 leading-relaxed">
                            {opt.note}
                          </p>
                          {selected && <div className="absolute top-3 right-3 size-2 rounded-lg bg-primary" />}
                        </label>;
            })}
                  </RadioGroup>
                </FormControl>
                <FormMessage className="font-mono text-xs tracking-wider mt-3" />
              </FormItem>} />}

        {}
        {step === 1 && <div className="space-y-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
              <FormField control={control} name="full_name" rules={{
            required: "Full name is required",
            minLength: {
              value: 2,
              message: "At least 2 characters"
            }
          }} render={({
            field
          }) => <FormItem>
                    <FormLabel className="font-mono text-[11px] tracking-[0.18em] uppercase text-foreground/60">
                      Full Name
                    </FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="John Smith" className="bg-transparent border-0 border-b border-foreground/30 px-0 h-12 text-foreground placeholder:text-foreground/30 focus-visible:border-primary focus-visible:ring-0 text-base" />
                    </FormControl>
                    <FormMessage className="font-mono text-xs tracking-wider" />
                  </FormItem>} />

              <FormField control={control} name="email" rules={{
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Enter a valid email"
            }
          }} render={({
            field
          }) => <FormItem>
                    <FormLabel className="font-mono text-[11px] tracking-[0.18em] uppercase text-foreground/60">
                      Email Address
                    </FormLabel>
                    <FormControl>
                      <Input {...field} type="email" placeholder="john@nexus.com" className="bg-transparent border-0 border-b border-foreground/30 px-0 h-12 text-foreground placeholder:text-foreground/30 focus-visible:border-primary focus-visible:ring-0 text-base" />
                    </FormControl>
                    <FormMessage className="font-mono text-xs tracking-wider" />
                  </FormItem>} />

              <FormField control={control} name="phone" rules={{
            required: "Phone is required"
          }} render={({
            field
          }) => <FormItem>
                    <FormLabel className="font-mono text-[11px] tracking-[0.18em] uppercase text-foreground/60">
                      Phone Number
                    </FormLabel>
                    <FormControl>
                      <Input {...field} type="tel" placeholder="+1 (555) 000-0000" className="bg-transparent border-0 border-b border-foreground/30 px-0 h-12 text-foreground placeholder:text-foreground/30 focus-visible:border-primary focus-visible:ring-0 text-base" />
                    </FormControl>
                    <FormMessage className="font-mono text-xs tracking-wider" />
                  </FormItem>} />

              <FormField control={control} name="preferred_date" rules={{
            required: "Please select a date"
          }} render={({
            field
          }) => <FormItem>
                    <FormLabel className="font-mono text-[11px] tracking-[0.18em] uppercase text-foreground/60">
                      Availability Date
                    </FormLabel>
                    <FormControl>
                      <Input {...field} type="date" className="bg-transparent border-0 border-b border-foreground/30 px-0 h-12 text-foreground focus-visible:border-primary focus-visible:ring-0 text-base [&::-webkit-calendar-picker-indicator]:invert" />
                    </FormControl>
                    <FormMessage className="font-mono text-xs tracking-wider" />
                  </FormItem>} />
            </div>

            {}
            <FormField control={control} name="preferred_date" render={({
          field
        }) => <FormItem>
                  <FormLabel className="font-mono text-[11px] tracking-[0.18em] uppercase text-foreground/60 flex items-center gap-2">
                    <CalendarDays className="size-3.5" />
                    Calendar Selection
                  </FormLabel>
                  <FormControl>
                    <div className="bg-muted border border-foreground/15 p-4 mt-2 inline-block">
                      <SafeHydrate><Calendar mode="single" selected={field.value ? new Date(field.value) : undefined} onSelect={d => field.onChange(d ? d.toISOString().split("T")[0] : "")} className="text-foreground [&_button]:text-foreground [&_.rdp-day_selected]:!bg-primary [&_.rdp-day_selected]:!text-primary-foreground" /></SafeHydrate>
                    </div>
                  </FormControl>
                </FormItem>} />
          </div>}

        {}
        {step === 2 && <div className="space-y-8">
            <SummaryRow control={control} name="service_type" label="SERVICE" />
            <SummaryRow control={control} name="full_name" label="NAME" />
            <SummaryRow control={control} name="email" label="EMAIL" />
            <SummaryRow control={control} name="phone" label="PHONE" />
            <SummaryRow control={control} name="preferred_date" label="DATE" />

            <FormField control={control} name="message" render={({
          field
        }) => <FormItem>
                  <FormLabel className="font-mono text-[11px] tracking-[0.18em] uppercase text-foreground/60">
                    Professional Summary
                  </FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Briefly describe your career goals or attach a link to your portfolio." rows={4} className="bg-transparent border-0 border-b border-foreground/30 px-0 text-foreground placeholder:text-foreground/30 focus-visible:border-primary focus-visible:ring-0 text-base resize-none" />
                  </FormControl>
                  <FormMessage className="font-mono text-xs tracking-wider" />
                </FormItem>} />

            <FormField control={control} name="consent" rules={{
          required: "Please confirm to continue"
        }} render={({
          field
        }) => <FormItem>
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input type="checkbox" checked={!!field.value} onChange={e => field.onChange(e.target.checked)} className="mt-1 size-4 accent-primary bg-transparent border border-foreground/40" />
                    <span className="text-sm text-foreground/80 leading-relaxed">
                      I agree to the processing of my data for recruitment purposes.
                      <span className="block font-default italic text-xs text-foreground/50 mt-1">
                        Nexus Talent Partners maintains strict confidentiality.
                      </span>
                    </span>
                  </label>
                  <FormMessage className="font-mono text-xs tracking-wider" />
                </FormItem>} />
          </div>}

        {submitError && <div className="p-4 border border-destructive/50 bg-destructive/10">
            <p className="font-mono text-xs tracking-wider text-destructive">{submitError}</p>
          </div>}

        {}
        <div className="flex items-center justify-between pt-8 border-t border-foreground/15 gap-4 flex-wrap">
          <Button type="button" variant="outline" onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} className="bg-transparent border-foreground/40 text-foreground hover:bg-transparent hover:border-primary hover:text-primary font-mono text-xs tracking-[0.08em] uppercase px-7 h-11 disabled:opacity-30">
            <ChevronLeft className="size-4" />
            Back
          </Button>

          {step < 2 ? <Button type="button" onClick={() => setStep(s => Math.min(2, s + 1))} className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs tracking-[0.08em] uppercase px-7 h-11">
              Continue
              <ChevronRight className="size-4" />
            </Button> : <Button type="submit" disabled={isSubmitting} className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs tracking-[0.08em] uppercase px-8 h-11">
              {isSubmitting ? <>
                  <Loader className="size-4 animate-spin" />
                  Submitting
                </> : <>
                  Submit Application
                  <span className="size-1.5 rounded-lg bg-white inline-block ml-1" />
                </>}
            </Button>}
        </div>
      </Form>
    </div>;
}
function SummaryRow({
  control,
  name,
  label
}: {
  control: any;
  name: string;
  label: string;
}) {
  return <FormField control={control} name={name} render={({
    field
  }) => <div className="flex items-baseline gap-6 py-3 border-b border-foreground/15">
          <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-foreground/50 w-28 shrink-0">
            {label}
          </span>
          <span className="font-default text-lg text-foreground flex-1 break-words">
            {field.value || <span className="italic text-foreground/30">— not provided —</span>}
          </span>
        </div>} />;
}